import type { ReactNode } from 'react';

export function Section({
  title,
  subtitle,
  children,
  disabled,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <section className={`panel${disabled ? ' panel--disabled' : ''}`}>
      <header className="panel__header">
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </header>
      <div className="panel__body">{children}</div>
    </section>
  );
}

export function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
  hint,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  format?: (value: number) => string;
  hint?: string;
}) {
  return (
    <label className="control">
      <span className="control__label">
        {label}
        <em>{format ? format(value) : value}</em>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {hint ? <small className="control__hint">{hint}</small> : null}
    </label>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="control control--toggle">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="control__label">{label}</span>
      {hint ? <small className="control__hint">{hint}</small> : null}
    </label>
  );
}

export function Select<T extends string>({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  hint?: string;
}) {
  return (
    <label className="control">
      <span className="control__label">{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hint ? <small className="control__hint">{hint}</small> : null}
    </label>
  );
}

export function ProgressBar({ value, label }: { value: number | null; label?: string }) {
  return (
    <div className="progress">
      <div
        className={`progress__fill${value === null ? ' progress__fill--indeterminate' : ''}`}
        style={value === null ? undefined : { width: `${Math.round(value * 100)}%` }}
      />
      {label ? <span className="progress__label">{label}</span> : null}
    </div>
  );
}
